import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Select, Empty } from "@/components/Kit";
import { Save } from "lucide-react";

export default function MarksEntryTab() {
  const [examId, setExamId] = useState("");
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("A");
  const [subject, setSubject] = useState("");
  const [rows, setRows] = useState(null);
  const [exam, setExam] = useState(null);

  const { data: exams = [] } = useQuery({ queryKey: ["exams"], queryFn: async () => (await api.get("/school/academics/exams")).data });
  const { data: classes = [] } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/school/academics/classes")).data });
  const { data: subjects = [] } = useQuery({ queryKey: ["subjects"], queryFn: async () => (await api.get("/school/academics/subjects")).data });
  const sections = classes.find((c) => c.name === cls)?.sections || ["A"];

  useEffect(() => {
    if (!examId || !cls || !subject) { setRows(null); return; }
    api.get(`/school/academics/exams/${examId}/marks`, { params: { class_name: cls, section: sec, subject } }).then(({ data }) => {
      setExam(data.exam);
      setRows(data.rows.map((r) => ({ ...r, marks: r.absent ? "AB" : r.marks ?? "" })));
    });
  }, [examId, cls, sec, subject]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/school/academics/exams/${examId}/marks`, {
      class_name: cls, section: sec, subject,
      marks: rows.map((r) => ({ student_id: r.id, marks: r.marks === "" || String(r.marks).toUpperCase() === "AB" ? null : Number(r.marks) })),
    })).data,
    onSuccess: (d) => toast.success(`Saved marks for ${d.saved} students`),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const set = (i, v) => setRows(rows.map((r, j) => (j === i ? { ...r, marks: v } : r)));
  const entered = rows?.filter((r) => r.marks !== "").length || 0;

  return (
    <div data-testid="marks-tab">
      <div className="ep-card mb-4 grid gap-3 p-4 sm:grid-cols-4">
        <Select label="Exam" value={examId} onChange={(e) => setExamId(e.target.value)} data-testid="mk-exam"><option value="">Select</option>{exams.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.academic_year} (max {e.max_marks})</option>)}</Select>
        <Select label="Class" value={cls} onChange={(e) => { setCls(e.target.value); setSec("A"); }} data-testid="mk-class"><option value="">Select</option>{classes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</Select>
        <Select label="Section" value={sec} onChange={(e) => setSec(e.target.value)} data-testid="mk-section">{sections.map((s) => <option key={s}>{s}</option>)}</Select>
        <Select label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mk-subject"><option value="">Select</option>{subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}</Select>
      </div>

      {!rows ? <div className="ep-card"><Empty>Select exam, class, section and subject to enter marks</Empty></div> : (
        <div className="ep-card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
            <p className="text-sm text-slate-300"><b className="text-white">{exam?.name}</b> · {subject} · Class {cls}-{sec} · Max <b className="text-white">{exam?.max_marks}</b> · <span className="text-slate-500">{entered}/{rows.length} entered</span></p>
            <button className="ep-btn" onClick={() => save.mutate()} disabled={save.isPending || !rows.length} data-testid="mk-save-btn"><Save size={15} /> {save.isPending ? "Saving…" : "Save Marks"}</button>
          </div>
          <table className="w-full text-sm" data-testid="mk-table">
            <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-2.5">#</th><th className="px-4 py-2.5">Adm No</th><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5 w-40">Marks (or AB)</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-b border-slate-800/50" data-testid={`mk-row-${r.admission_no}`}>
                  <td className="px-4 py-2 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-400">{r.admission_no}</td>
                  <td className="px-4 py-2 font-medium text-white">{r.name}</td>
                  <td className="px-4 py-2"><input className="ep-input !py-1.5 text-center font-mono" value={r.marks} onChange={(e) => set(i, e.target.value.toUpperCase())} placeholder={`0-${exam?.max_marks}`} data-testid={`mk-input-${r.admission_no}`} /></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={4}><Empty>No students in this class-section</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
